import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import type { Photo } from '@/types/listing';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Local calendar date as YYYY-MM-DD (the viewer's timezone). */
export function localTodayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * A listing counts as "new" for 7 days after its list date. Dates compare as
 * ISO strings/day counts — never as instants (timezone-shift gotcha).
 */
export function isNewListing(listDate: string | undefined, todayISO: string): boolean {
  if (!listDate) return false;
  const day = listDate.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day) || day > todayISO) return false;
  const diff = (Date.parse(todayISO + 'T12:00:00Z') - Date.parse(day + 'T12:00:00Z')) / 86400000;
  return diff <= 7;
}

/** Whole days from an ISO date to todayISO (calendar-day diff; ≥0). */
export function daysSince(dateStr: string | undefined, todayISO: string): number {
  if (!dateStr) return 0;
  const day = dateStr.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return 0;
  const diff = (Date.parse(todayISO + 'T12:00:00Z') - Date.parse(day + 'T12:00:00Z')) / 86400000;
  return diff > 0 ? Math.floor(diff) : 0;
}

export function formatPrice(price: number): string {
  if (price >= 1000000) {
    const m = price / 1000000;
    return `$${m.toFixed(Number.isInteger(m) ? 0 : 1)}M`;
  }
  if (price >= 1000) {
    const k = Math.round(price / 1000);
    // A value like $999,888 rounds to 1,000K — read that as $1.0M, not "$1000K".
    if (k >= 1000) return `$${(k / 1000).toFixed(1)}M`;
    return `$${k}K`;
  }
  return `$${price.toLocaleString()}`;
}

export function formatPriceFull(price: number): string {
  return `$${price.toLocaleString()}`;
}

export function formatSqft(sqft: number): string {
  return `${sqft.toLocaleString()} sqft`;
}

export function formatBaths(baths: number): string {
  if (baths % 1 === 0) {
    return baths.toString();
  }
  return baths.toFixed(1);
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

export function generateListingSlug(listing: {
  address: string;
  city: string;
  zip: string;
  mlsListingId: string;
}): string {
  const addressSlug = slugify(listing.address);
  const citySlug = slugify(listing.city);
  return `/homes/${citySlug}/${addressSlug}-${listing.zip}/mls-${listing.mlsListingId}`;
}

export function getPropertyTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    single_family: 'Single Family',
    condo: 'Condo',
    townhouse: 'Townhouse',
    multi_family: 'Multi-Family',
    mobile: 'Mobile Home',
    land: 'Land',
    // The MLS's own spellings, verbatim as they arrive in the facet map. Without
    // these a buyer sees a chip reading "farm / ranch" — lowercase, with the
    // slash — which is the feed's vocabulary leaking onto the page.
    'other residential': 'Other',
    'farm / ranch': 'Farm / Ranch',
    'manufactured home': 'Manufactured Home',
  };
  if (labels[type]) return labels[type];
  // An unseen type must still render as SOMETHING a person can read: an unlabelled
  // chip cannot be interpreted, and the feed will keep inventing subclasses.
  return type
    .split(/[\s_]+/)
    .filter(Boolean)
    .map((w) => (w === '/' ? w : w[0].toUpperCase() + w.slice(1)))
    .join(' ');
}

// `getStatusLabel` / `getStatusColor` lived here and are gone: they were a second
// availability vocabulary (raw MLS words — "Expired", "Active Contingent" — in
// paper-canvas tints that vanish over a photo). `lib/availability` is the only
// one now, shared in spirit and copy with the iOS `ListingAvailability`. (W16 R151)

// MLS data stores city names with inconsistent casing ("SAN JOSE" and
// "San Jose" appear as separate rows) — merge them by slug for display.
export function mergeCitiesBySlug(
  cities: Array<{ name: string; count: number }>
): Array<{ name: string; slug: string; count: number }> {
  const merged = new Map<string, { name: string; slug: string; count: number }>();
  for (const city of cities) {
    const slug = slugify(city.name);
    if (!slug) continue;
    const existing = merged.get(slug);
    if (existing) {
      existing.count += city.count;
    } else {
      merged.set(slug, { name: titleCaseCity(city.name), slug, count: city.count });
    }
  }
  return [...merged.values()].sort((a, b) => b.count - a.count);
}

// MLS data often stores city names in all caps ("SAN JOSE") — display-case them.
export function titleCaseCity(city: string): string {
  return city
    .toLowerCase()
    .split(/(\s|-)/)
    .map((part) =>
      /^(\s|-)$/.test(part) ? part : part.charAt(0).toUpperCase() + part.slice(1)
    )
    .join('');
}

export function debounce<T extends (...args: string[]) => void>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;

  return (...args: Parameters<T>) => {
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => func(...args), wait);
  };
}

export function pluralize(count: number, singular: string, plural?: string): string {
  if (count === 1) return `${count} ${singular}`;
  return `${count} ${plural || singular + 's'}`;
}

// Local branded placeholder for listings without photos (no external image dependency)
export function getPlaceholderImage(_listingId?: string, _width = 800, _height = 600): string {
  return '/placeholder-home.svg';
}

export function getPlaceholderImages(listingId: string, count: number): Photo[] {
  return Array.from({ length: Math.min(count, 10) }, (_, i) => ({
    url: getPlaceholderImage(`${listingId}-${i}`),
    order: i,
  }));
}
